import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Users, CreditCard, DollarSign, Calendar, MapPin, Mail, Phone, FileText, Edit2, Save, X, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { formatDateUTC } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COUNTRIES = [
  "Afganistán", "Albania", "Alemania", "Andorra", "Angola", "Antigua y Barbuda", "Arabia Saudita", "Argelia", "Argentina", "Armenia",
  "Australia", "Austria", "Azerbaiyán", "Bahamas", "Bangladés", "Barbados", "Baréin", "Bélgica", "Belice", "Benín",
  "Bielorrusia", "Birmania", "Bolivia", "Bosnia y Herzegovina", "Botsuana", "Brasil", "Brunéi", "Bulgaria", "Burkina Faso", "Burundi",
  "Bután", "Cabo Verde", "Camboya", "Camerún", "Canadá", "Catar", "Chad", "Chile", "China", "Chipre",
  "Colombia", "Comoras", "Corea del Norte", "Corea del Sur", "Costa de Marfil", "Costa Rica", "Croacia", "Cuba", "Dinamarca", "Dominica",
  "Ecuador", "Egipto", "El Salvador", "Emiratos Árabes Unidos", "Eritrea", "Eslovaquia", "Eslovenia", "España", "Estados Unidos", "Estonia",
  "Etiopía", "Filipinas", "Finlandia", "Fiyi", "Francia", "Gabón", "Gambia", "Georgia", "Ghana", "Granada",
  "Grecia", "Guatemala", "Guinea", "Guinea Ecuatorial", "Guinea-Bisáu", "Guyana", "Haití", "Honduras", "Hungría", "India",
  "Indonesia", "Irak", "Irán", "Irlanda", "Islandia", "Islas Marshall", "Islas Salomón", "Israel", "Italia", "Jamaica",
  "Japón", "Jordania", "Kazajistán", "Kenia", "Kirguistán", "Kiribati", "Kuwait", "Laos", "Lesoto", "Letonia",
  "Líbano", "Liberia", "Libia", "Liechtenstein", "Lituania", "Luxemburgo", "Macedonia del Norte", "Madagascar", "Malasia", "Malaui",
  "Maldivas", "Malí", "Malta", "Marruecos", "Mauricio", "Mauritania", "México", "Micronesia", "Moldavia", "Mónaco",
  "Mongolia", "Montenegro", "Mozambique", "Namibia", "Nauru", "Nepal", "Nicaragua", "Níger", "Nigeria", "Noruega",
  "Nueva Zelanda", "Omán", "Países Bajos", "Pakistán", "Palaos", "Palestina", "Panamá", "Papúa Nueva Guinea", "Paraguay", "Perú",
  "Polonia", "Portugal", "Reino Unido", "República Centroafricana", "República Checa", "República del Congo", "República Democrática del Congo", "República Dominicana", "Ruanda", "Rumania",
  "Rusia", "Samoa", "San Cristóbal y Nieves", "San Marino", "San Vicente y las Granadinas", "Santa Lucía", "Santo Tomé y Príncipe", "Senegal", "Serbia", "Seychelles",
  "Sierra Leona", "Singapur", "Siria", "Somalia", "Sri Lanka", "Suazilandia", "Sudáfrica", "Sudán", "Sudán del Sur", "Suecia",
  "Suiza", "Surinam", "Tailandia", "Tanzania", "Tayikistán", "Timor Oriental", "Togo", "Tonga", "Trinidad y Tobago", "Túnez",
  "Turkmenistán", "Turquía", "Tuvalu", "Ucrania", "Uganda", "Uruguay", "Uzbekistán", "Vanuatu", "Vaticano", "Venezuela",
  "Vietnam", "Yemen", "Yibuti", "Zambia", "Zimbabue"
];

export default function AdminEmpresaDetalle() {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCompanyDetail();
  }, [companyId]);

  const fetchCompanyDetail = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const response = await axios.get(`${API_URL}/admin/companies/${companyId}/detail`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setEditData(response.data.company);
    } catch (error) {
      toast.error('Error al cargar datos de la empresa');
      navigate('/admin/empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    setEditData({...data.company});
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditData({...data.company});
    setIsEditing(false);
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.put(`${API_URL}/admin/companies/${companyId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Empresa actualizada correctamente');
      setIsEditing(false);
      fetchCompanyDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar empresa');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { company, users, memberships, payments } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-empresa-detalle">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin/empresas')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Detalle de Empresa</h1>
            <p className="text-slate-600">{company.razon_social || company.nombre_fantasia}</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        {/* Datos de la Empresa */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <Building2 className="text-violet-600" size={24} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Datos de la Empresa</h2>
            </div>
            {!isEditing ? (
              <button 
                onClick={handleStartEdit}
                className="flex items-center gap-2 px-4 py-2 bg-violet-50 text-violet-600 hover:bg-violet-100 rounded-lg font-semibold transition-colors"
                data-testid="btn-edit-company"
              >
                <Edit2 size={18} />
                Editar
              </button>
            ) : (
              <div className="flex gap-2">
                <button 
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg font-semibold transition-colors"
                >
                  <X size={18} />
                  Cancelar
                </button>
                <button 
                  onClick={handleSaveCompany}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            )}
          </div>
          
          {isEditing ? (
            // Edit Mode
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-violet-50 rounded-xl p-4">
                <p className="text-sm text-violet-600 mb-1">Código de Empresa</p>
                <p className="font-mono font-bold text-violet-700 text-lg">{company.codigo_empresa}</p>
                <p className="text-xs text-violet-500 mt-1">No editable</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre de Fantasía</label>
                <input 
                  type="text" 
                  value={editData.nombre_fantasia || ''} 
                  onChange={(e) => setEditData({...editData, nombre_fantasia: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Razón Social</label>
                <input 
                  type="text" 
                  value={editData.razon_social || ''} 
                  onChange={(e) => setEditData({...editData, razon_social: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de Documento</label>
                <select 
                  value={editData.tipo_documento || ''} 
                  onChange={(e) => setEditData({...editData, tipo_documento: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="">Seleccione...</option>
                  <option value="NIT">NIT</option>
                  <option value="CC">Cédula de Ciudadanía</option>
                  <option value="CE">Cédula de Extranjería</option>
                  <option value="RUT">RUT</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Número de Documento</label>
                <input 
                  type="text" 
                  value={editData.numero_documento || ''} 
                  onChange={(e) => setEditData({...editData, numero_documento: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">País</label>
                <select 
                  value={editData.pais || ''} 
                  onChange={(e) => setEditData({...editData, pais: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="">Seleccione un país</option>
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Dirección</label>
                <input 
                  type="text" 
                  value={editData.direccion || ''} 
                  onChange={(e) => setEditData({...editData, direccion: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div className="md:col-span-2 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <label className="block text-sm font-medium text-amber-700 mb-2 flex items-center gap-2">
                  <Mail size={16} />
                  Email de Respaldo (recibe copia de consentimientos firmados)
                </label>
                <input 
                  type="email" 
                  value={editData.email_respaldo || ''} 
                  onChange={(e) => setEditData({...editData, email_respaldo: e.target.value})}
                  placeholder="ejemplo@clinica.com"
                  className="w-full px-4 py-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                />
                <p className="text-xs text-amber-600 mt-2">Este correo recibirá una copia de cada consentimiento firmado</p>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-500 mb-1">Código de Empresa</p>
                <p className="font-mono font-bold text-violet-600 text-lg">{company.codigo_empresa}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Nombre de Fantasía</p>
                <p className="font-semibold text-slate-900">{company.nombre_fantasia || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Razón Social</p>
                <p className="font-semibold text-slate-900">{company.razon_social || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Tipo de Documento</p>
                <p className="font-semibold text-slate-900">{company.tipo_documento || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Número de Documento</p>
                <p className="font-mono font-semibold text-slate-900">{company.numero_documento || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">País</p>
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  <Globe size={16} className="text-slate-400" />
                  {company.pais || 'N/A'}
                </p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-slate-500 mb-1">Dirección</p>
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  <MapPin size={16} className="text-slate-400" />
                  {company.direccion || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Estado</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  company.activo ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                }`}>
                  {company.activo ? 'Activada' : 'Desactivada'}
                </span>
              </div>
              <div className="md:col-span-3 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <p className="text-sm text-amber-600 mb-1 flex items-center gap-2">
                  <Mail size={16} />
                  Email de Respaldo
                </p>
                <p className="font-semibold text-amber-900">{company.email_respaldo || 'No configurado'}</p>
                <p className="text-xs text-amber-600 mt-1">Recibe copia de cada consentimiento firmado</p>
              </div>
            </div>
          )}
        </section>

        {/* Usuarios */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="text-blue-600" size={24} />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Usuarios ({users.length})</h2>
          </div>

          {users.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No hay usuarios registrados</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Nombre Completo</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Usuario</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Celular</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Rol</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Fecha Creación</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{user.nombre_completo}</td>
                      <td className="px-4 py-3 text-sm font-mono text-violet-600">{user.username}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Mail size={14} className="text-slate-400" />
                          {user.email}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Phone size={14} className="text-slate-400" />
                          {user.celular}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          user.role === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {user.role === 'admin' ? 'Administrador' : 'Usuario'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {user.created_at ? format(new Date(user.created_at), 'dd/MM/yyyy HH:mm') : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Historial de Membresías */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <CreditCard className="text-amber-600" size={24} />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Historial de Membresías ({memberships.length})</h2>
          </div>

          {memberships.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No hay membresías registradas</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Tipo</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Fecha Activación</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Fecha Renovación</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-900">Consentimientos</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-900">Estado Pago</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-900">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {memberships.map((membership) => (
                    <tr key={membership.id} className={`hover:bg-slate-50 ${membership.activo ? 'bg-emerald-50/50' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold">
                          {membership.tipo_membresia || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} className="text-slate-400" />
                          {formatDateUTC(membership.fecha_activacion)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDateUTC(membership.fecha_renovacion)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-mono font-semibold">
                          {membership.consentimientos_usados || 0} / {membership.cantidad_consentimientos === -1 ? '∞' : membership.cantidad_consentimientos || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          membership.estado_pago === 'Vigente' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {membership.estado_pago || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          membership.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {membership.activo ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Historial de Pagos */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <DollarSign className="text-emerald-600" size={24} />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Historial de Pagos ({payments.length})</h2>
          </div>

          {payments.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No hay pagos registrados</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Fecha de Pago</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Tipo de Membresía</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Medio de Pago</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">Valor Pagado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} className="text-slate-400" />
                          {payment.fecha_pago ? format(new Date(payment.fecha_pago), 'dd/MM/yyyy') : 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold">
                          {payment.tipo_membresia || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900 font-medium">{payment.medio_pago || 'N/A'}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-emerald-600">
                        ${payment.valor_pagado?.toLocaleString() || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
